import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Datepicker from 'react-datepicker';
import moment from 'moment';
import { cloneDeep } from 'lodash';
import PropTypes, { any } from 'prop-types';

import { TitleSwitch } from '../../Form';
import MatchOutCome from '../../MatchOutCome/MatchOutcomeV2';
import Player from '../Player';
import PickleballSet from './PickleballSetV2';

import { DisplayStates, OutComeTypes, DefaultSets } from '../../../utils/postScoreConstants';
import { resultToScore, getPostScoreData, validatePickleballPostScoreData, matchPlayersEqual, computeMatchStatus, resolveMatchOutcome, parseMatchDate, resolveExcludeFromRating, resolveIsDoubles, getRedirectUrl, areScoresValid } from '../../../utils/postScore';
import PopupV3, { PopupTypes } from '../../Popup/PopupV3';
import SwapIcon from '../../../pages/DrawEditPage/EventDeskPostScore/SwapIcon';

import styles from '../postScore.scss';

const _ = { cloneDeep };

const PickleballPostScoreForm = (props) => {
    const {
        isEvent,
        isEdit,
        isPlayerPostVerifiedEvent,
        isFirstMatch,
        isLastMatch,
        defaultCountsForRating,
        playerPost,
        playerMatchPost,
        showCountsForRating,
        timeZone,
        maxResultDateUtc,
        isDualMatch,
        matchObj,
        teamsDict,
        playersDictByMemberId,
        clubTierType,
        user,
        isInPopup,
        showExitButton,
        isSaving,
        forceDoubles,
        isExpanded,
    } = props;

    const isEditable = !(isEvent || isEdit);
    const { redirectUrlPage } = useSelector((state) => state.postScore);

    const [match, setMatch] = useState(null);
    const [date, setDate] = useState(null);
    const [isWinner, setIsWinner] = useState(false);
    const [winningTeam, setWinningTeam] = useState(null);
    const [matchOutCome, setMatchOutCome] = useState(null);
    const [isDoubles, setIsDoubles] = useState(forceDoubles || false);
    const [isReverseView, setIsReverseView] = useState(false);
    const [sets, setSets] = useState(DefaultSets);
    const [displayState, setDisplayState] = useState(null);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isUpdated, setIsUpdated] = useState(false);
    const [excludeFromRating, setExcludeFromRating] = useState(false);
    const [isVerifiedResult, setisVerifiedResult] = useState(false);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [hasNoScore, setHasNoScore] = useState(false);

    const updateMatchStatus = (outcome) => {
        const result = computeMatchStatus(sets, outcome, match, teamsDict, playersDictByMemberId);
        setHasNoScore(result.hasNoScore);
        setIsWinner(result.isWinner);
        setIsCompleted(result.isCompleted);
        setWinningTeam(result.winningTeam);
        setDisplayState(result.displayState);
        setMatchOutCome(outcome);
    };

    useEffect(() => {
        updateMatchStatus(matchOutCome);
    }, [sets]);

    const shouldUpdateMatch = (newMatchObj) => {
        if (match && newMatchObj) {
            return !matchPlayersEqual(match, newMatchObj);
        }
        return true;
    };

    useEffect(() => {
        if (!shouldUpdateMatch(matchObj)) {
            return;
        }

        setMatch(matchObj);
        if (matchObj?.result) {
            setSets(resultToScore(matchObj, teamsDict, playersDictByMemberId));
            setMatchOutCome(resolveMatchOutcome(matchObj.result));
            setExcludeFromRating(false);
        } else {
            setSets(DefaultSets);
            setMatchOutCome(null);
            setIsWinner(false);
            setIsCompleted(false);
            setWinningTeam(null);
            setDisplayState(DisplayStates.NO_SCORES);
            setExcludeFromRating(resolveExcludeFromRating(isEvent, defaultCountsForRating, excludeFromRating));
        }

        setDate(parseMatchDate(matchObj, timeZone));
        setIsUpdated(false);
    }, [matchObj, isEvent, defaultCountsForRating]);

    useEffect(() => {
        setIsDoubles(resolveIsDoubles(forceDoubles, isEvent, isEdit, playerMatchPost, matchObj));
    }, [isEvent, isEdit, playerMatchPost, matchObj, forceDoubles]);

    const handleDateChange = (newDate) => {
        setDate(newDate);
        setIsUpdated(true);
    };

    const reverseView = () => {
        const newMatch = _.cloneDeep(match);
        const { pos1player1, pos1player2, pos2player1, pos2player2 } = newMatch;
        setMatch({
            ...newMatch,
            pos1player1: pos2player1,
            pos1player2: pos2player2,
            pos2player1: pos1player1,
            pos2player2: pos1player2,
        });
        setIsUpdated(true);
        setIsReverseView(!isReverseView);
        updateMatchStatus(matchOutCome);
    };

    const isPostDisabled = displayState !== DisplayStates.NO_SCORES || isWinner || matchOutCome === OutComeTypes.ABANDONED || matchOutCome === OutComeTypes.DOUBLEWALKOVER;
    const postDisabled = () => {
        let disabled = false;
        if (isDoubles) {
            disabled = match && !(match.pos1player2 && match.pos2player1 && match.pos2player2 && isPostDisabled);
        } else {
            disabled = match && !(match.pos2player1 && isPostDisabled);
        }

        return disabled || !areScoresValid(sets, matchOutCome) || submitting;
    };

    const handlePostClick = async (direction = null) => {
        let data = null;
        if (isUpdated && !postDisabled()) {
            data = getPostScoreData({
                match,
                sets,
                date,
                isEvent,
                isEdit,
                isDoubles,
                isWinner,
                isCompleted,
                isVerifiedResult,
                matchOutCome,
                winningTeam,
                playerMatchPost,
                user,
                excludeFromRating,
            });

            const validation = validatePickleballPostScoreData({
                postScoreData: data,
                sets,
                isDoubles,
                isWinner,
                isCompleted,
                isVerifiedResult,
                matchOutCome,
                clubTierType,
            });
            if (validation?.valid) {
                setSubmitting(true);
                await props.handlePostClick(data, direction);
            } else {
                setError(validation?.error);
            }
        } else {
            setSubmitting(true);
            await props.handlePostClick(data, direction);
        }
        setSubmitting(false);
    };

    const handleOutcomeSelect = (newIsWinner, newIsCompleted, newMatchOutCome, newWinningTeam) => {
        if (displayState === DisplayStates.WINNER_DECIDED && newMatchOutCome === OutComeTypes.COMPLETE) {
            updateMatchStatus(newMatchOutCome);
        } else if (displayState === DisplayStates.WINNER_NOT_DECIDED && newMatchOutCome === OutComeTypes.INCOMPLETE) {
            updateMatchStatus(newMatchOutCome);
        } else {
            setIsUpdated(true);
            setIsWinner(newIsWinner);
            setIsCompleted(newIsCompleted);
            setMatchOutCome(newMatchOutCome);
            setWinningTeam(newWinningTeam);
        }
    };

    const handleInputChange = (set, key, event) => {
        const { value } = event.target;
        const newSets = _.cloneDeep(sets);
        const setIndex = newSets.findIndex(s => s.id === set.id);
        newSets[setIndex][key] = value;
        setSets(newSets);
        if (matchOutCome !== OutComeTypes.FORFEIT) {
            setMatchOutCome(null);
        }
        setIsUpdated(true);
    };

    const clearFormData = () => {
        setIsDoubles(forceDoubles || false);
        setDate(moment());
        setMatch(null);
        setIsWinner(false);
        setIsCompleted(false);
        setMatchOutCome(null);
        setWinningTeam(null);
        setDisplayState(DisplayStates.NO_SCORES);
        setIsUpdated(false);
        setExcludeFromRating(resolveExcludeFromRating(false, defaultCountsForRating, excludeFromRating));
        setisVerifiedResult(false);
        setSets(DefaultSets);

        props.clearMatchData();
    };

    useEffect(() => {
        if (props.externalSaveClick === true) {
            handlePostClick();
        }
    }, [props.externalSaveClick]);

    useEffect(() => {
        if (props.externalClearFormData === true) {
            clearFormData();
        }
    }, [props.externalClearFormData]);

    let maxMatchDate = moment();
    if (isEvent && maxResultDateUtc) {
        maxMatchDate = moment(maxResultDateUtc).tz(timeZone.value);
    }

    const getNextButtonLabel = () => {
        if (isSaving) {
            return 'Saving...';
        }
        return `${isUpdated ? 'Save & ' : ''}${isLastMatch ? 'Exit' : 'Next'}`;
    };

    const getExitButtonLabel = () => {
        if (isSaving) {
            return 'Saving...';
        }
        return `${isUpdated ? 'Save & ' : ''} Exit`;
    };

    const redirectUrl = getRedirectUrl(showCountsForRating, redirectUrlPage);
    const isPrevButtonDisabled = !areScoresValid(sets, matchOutCome) || isFirstMatch;
    const isDisabled = !areScoresValid(sets, matchOutCome) || isSaving;

    return (
        <>
            <div className="container">
                <div className="row">
                    <div className={isInPopup ? 'col-12' : `col-md-8 offset-md-2`}>
                        {!isInPopup && (
                            <>
                                <div className={`${styles.matchTitle}`}>
                                    Match Score{' '}
                                    <span className={`ml4 ${isPlayerPostVerifiedEvent ? 'verified check-mark' : ''}`} />
                                </div>
                                <hr className={`${styles.divider}`} />
                            </>
                        )}
                        <div className={isInPopup ? 'mt32' : ''}>
                            <div className="row">
                                <Player
                                    match={match}
                                    isDualMatch={isDualMatch}
                                    isDoubles={isDoubles}
                                    isEditable={isEditable}
                                    playerPost={playerPost}
                                    isWinner={isWinner}
                                    winningTeam={winningTeam}
                                    matchOutCome={matchOutCome}
                                    isReverseView={isReverseView}
                                    position={'pos1player1'}
                                    addPlayer={props.addPlayer}
                                    isInPopup={isInPopup}
                                />
                                {isDoubles && (
                                    <Player
                                        match={match}
                                        isDualMatch={isDualMatch}
                                        isDoubles={isDoubles}
                                        isEditable={isEditable}
                                        playerPost={playerPost}
                                        isWinner={isWinner}
                                        winningTeam={winningTeam}
                                        matchOutCome={matchOutCome}
                                        isReverseView={isReverseView}
                                        position={'pos1player2'}
                                        addPlayer={props.addPlayer}
                                        isInPopup={isInPopup}
                                    />
                                )}
                            </div>
                            <div className="sets d-flex justify-content-center align-items-center">
                                {sets.map((set) => {
                                    return (
                                        <PickleballSet
                                            key={set}
                                            set={set}
                                            onInputChange={handleInputChange}
                                        />
                                    );
                                })}
                                <button
                                    onClick={() => reverseView()}
                                    className={`${styles.swapButton}`}
                                    type="button"
                                    data-testid="post-score-pickleball.reverse-view-btn"
                                >
                                    <SwapIcon />
                                </button>
                            </div>
                            <div className="row">
                                <Player
                                    match={match}
                                    isDualMatch={isDualMatch}
                                    isDoubles={isDoubles}
                                    isEditable={isEditable}
                                    playerPost={playerPost}
                                    isWinner={isWinner}
                                    winningTeam={winningTeam}
                                    matchOutCome={matchOutCome}
                                    isReverseView={isReverseView}
                                    position={'pos2player1'}
                                    addPlayer={props.addPlayer}
                                    isInPopup={isInPopup}
                                />

                                {isDoubles && (
                                    <Player
                                        match={match}
                                        isDualMatch={isDualMatch}
                                        isDoubles={isDoubles}
                                        isEditable={isEditable}
                                        playerPost={playerPost}
                                        isWinner={isWinner}
                                        winningTeam={winningTeam}
                                        matchOutCome={matchOutCome}
                                        isReverseView={isReverseView}
                                        position={'pos2player2'}
                                        addPlayer={props.addPlayer}
                                        isInPopup={isInPopup}
                                    />
                                )}
                            </div>
                            <div className="row jcc mt8">
                                <a
                                    href={redirectUrl}
                                    className={`add-doubles text-small fw600`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Learn About Scoring
                                </a>
                            </div>
                            <hr className={`${isInPopup ? styles.popupDivider : styles.divider}`} />
                            <div className="row">
                                <MatchOutCome
                                    onSelect={handleOutcomeSelect}
                                    sets={sets}
                                    doubles={isDoubles}
                                    user={user}
                                    postScore={match}
                                    displayState={displayState}
                                    isCompleted={isCompleted}
                                    matchOutCome={matchOutCome}
                                    winningTeam={winningTeam}
                                    isWinner={isWinner}
                                    isEvent={isEvent}
                                    isExpanded={isExpanded}
                                    hasNoScore={hasNoScore}
                                    isPickleball
                                    {...props}
                                />
                            </div>
                            {isInPopup && (
                                <hr className={`${styles.popupDivider} mt0`} />
                            )}
                            {!isEvent && showCountsForRating && (
                                <div className={`row`}>
                                    <div className={`col-md-6 offset-md-3`}>
                                        <TitleSwitch
                                            id="excludeFromRating"
                                            formGroupCssClass={`mt16`}
                                            label="Counts For Rating"
                                            hoverText="If turned off, result will show on profiles but not count towards player ratings."
                                            value={!excludeFromRating}
                                            onChange={() => {
                                                setExcludeFromRating(!excludeFromRating);
                                                setIsUpdated(true);
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="row">
                    {!isInPopup && (
                        <div className={`col-md-8 offset-md-2 ${isInPopup && 'p0 mt32'} pb0`}>
                            <div className={styles.content}>
                                {!isDualMatch && !isInPopup && (
                                    <h5>Match Date</h5>
                                )}
                                <div className="row">
                                    {!isDualMatch && !isInPopup && (
                                        <div className="col-md-6 offset-md-3">
                                            <Datepicker
                                                className="form-control date-picker-input clickable"
                                                selected={date}
                                                onChange={handleDateChange}
                                                dateFormat="dddd, MMMM D"
                                                maxDate={maxMatchDate}
                                                disabled={typeof props.match === 'object'}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="row">
                                    {!props.isEvent && (
                                        <div className={'col-md-6 offset-md-3'}>
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-xl text-uppercase post-score "
                                                disabled={postDisabled()}
                                                onClick={() => handlePostClick()}
                                                data-testid="post-score-pickleball.post-score-btn"
                                            >
                                                {isEdit ? 'Update Score' : 'Post Score'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={`${isInPopup ? 'col-12 d-flex flex-row jcc mb32' : 'col-md-4 offset-md-4 d-flex flex-row jcc pb32'}`}>
                        {props.isEvent && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-primary text-uppercase post-score mr16"
                                    disabled={isPrevButtonDisabled}
                                    onClick={() => handlePostClick('prev')}
                                    data-testid="post-score-pickleball.prev-btn"
                                >
                                    {isUpdated ? 'Save & Prev' : 'Prev'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary text-uppercase post-score"
                                    disabled={isDisabled}
                                    onClick={() => handlePostClick('next')}
                                    data-testid="post-score-pickleball.next-btn"
                                >
                                    {getNextButtonLabel()}
                                </button>
                                {showExitButton && !isLastMatch && (
                                    <button
                                        type="button"
                                        className="btn btn-primary text-uppercase post-score ml16"
                                        disabled={isDisabled}
                                        onClick={() => handlePostClick()}
                                        data-testid="post-score-pickleball.exit-btn"
                                    >
                                        {getExitButtonLabel()}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
            <PopupV3
                title="Error"
                type={PopupTypes.ERROR}
                isOpen={!!error}
                onConfirmClick={() => setError(null)}
            />
        </>
    );
};

PickleballPostScoreForm.propTypes = {
    matchObj: PropTypes.objectOf(any).isRequired,
};

export default PickleballPostScoreForm;
